/* THE DRAFTING SCREEN — writing one block with Brill, shared by every panel
 * (spec R3 / step 4, and item 4 on her list, 2026-09-16: "so we can get
 * consistency there").
 *
 * This is the website's drafting screen lifted out UNCHANGED IN BEHAVIOUR — her
 * rule, "website is the most complete … use that for everything":
 *   · chat on the left, the block's fields on the right; the split set by the
 *     Half / Two thirds / Full control or the drag bar, and a Chat / Fields
 *     toggle when only one fits;
 *   · Brill's draft waits in a lock card — "Lock it into the …" / "Keep
 *     chatting" — and nothing reaches the fields until she presses it (rule 4);
 *   · her own typing saves as she leaves each field, and the fields' header
 *     says Saved, or says why not (rule 1);
 *   · the pager moves block to block without leaving the chat;
 *   · NEXT, beside Send, goes to the next block with nothing written — the
 *     Accelerator's "on to the next section", which is what she is used to
 *     (her ask, 2026-09-16). See `nextStep`.
 *   · the context chips say what Brill can see.
 *
 * NOTHING HERE KNOWS WHAT A PAGE, A MODULE OR A SEQUENCE IS. The panel hands in
 * the kind, the content, how to ask Brill, and how to read her answer.
 */
import { useEffect, useRef, useState } from "react";
import { useSession } from "@clerk/clerk-react";
import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight, CornerUpLeft } from "lucide-react";
import { CHAT_HEIGHT_DRAFTING, CHAT_HEIGHT_PHONE } from "../../constants/layout";
import { useVoiceRecorder } from "../../utils/voiceDrafts";
import { SectionFields, SuggestedCopy } from "./fields";
import { ALL_LIBRARY_KINDS, LibraryPickButton } from "./libraryPicker";
import { kindOf } from "../../utils/library";
import { B, blockIcon, btnGhost } from "./tokens";
import { ChatPane, SavedTick, useTwoPane } from "./ui";
import { PaneToggle, SPLIT_FULL, SplitDragBar, SplitModeControl, useSplitRatio } from "./shell";


/* Block to block without going back out to the list, which is how a record
   actually gets written in one sitting. A dot per block — teal written, grey
   not — and the one she is on is a bar. */
export function BlockPager({ items = [], index, onGo, noun = "Section" }) {
  const arrow = (dir, disabled) => (
    <button onClick={() => !disabled && onGo(index + dir)} disabled={disabled}
      aria-label={dir < 0 ? `Previous ${noun.toLowerCase()}` : `Next ${noun.toLowerCase()}`}
      style={{ ...blockIcon, opacity: disabled ? 0.3 : 1, cursor: disabled ? "default" : "pointer" }}>
      {dir < 0 ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
    </button>
  );
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      {arrow(-1, index <= 0)}
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        {items.map((it, i) => (
          /* Written or not — a block has no Done of its own (Done is marked on
             the record, 2026-09-15). */
          <button key={it.id} onClick={() => onGo(i)} title={it.label} aria-label={`Go to ${it.label}`}
            style={{ width: i === index ? 16 : 7, height: 7, padding: 0, borderRadius: 4, border: "none", background: it.written ? B.teal : B.ghost, cursor: "pointer", transition: "width 150ms ease" }} />
        ))}
      </div>
      <span style={{ fontSize: 12, color: B.faint, whiteSpace: "nowrap" }}>{noun} {index + 1} of {items.length}</span>
      {arrow(1, index >= items.length - 1)}
    </div>
  );
}


/**
 * Where Next goes from the block she is on.
 *
 * The next block with NOTHING WRITTEN, looking forward first and then back to
 * the start — an outstanding block is offered, never stepped over (rule 11,
 * `09ca453`). When every other block has words, Next becomes the way back out,
 * so she is never left without one.
 *
 * @returns { kind: "block", id, label } | { kind: "back" } | null (no pager)
 */
export function nextStep(pager) {
  if (!pager || !pager.items?.length) return null;
  const { items, index } = pager;
  const later = items.slice(index + 1).find((it) => !it.written);
  const earlier = items.slice(0, Math.max(0, index)).find((it) => !it.written);
  const target = later || earlier;
  if (target) return { kind: "block", id: target.id, label: target.label };
  /* "All written" only when it is true of THIS block too — the only unwritten
     block being the one she is on is not everything written. */
  return { kind: "back", allWritten: !!items[index]?.written };
}


/* The switch between the two scopes — "Touch | Sequence". Same
   segmented look as the pane toggle beside it. */
function ScopeSwitch({ scope, setScope, blockLabel, recordLabel, compact = false }) {
  const seg = (on) => ({
    padding: compact ? "6px 9px" : "8px 16px", borderRadius: 20, fontSize: compact ? 11 : 12.5, fontWeight: on ? 700 : 600,
    cursor: "pointer", fontFamily: "inherit", border: "none",
    background: on ? B.surface : "transparent", color: on ? B.text : B.dim,
    boxShadow: on ? "0 1px 3px rgba(0,0,0,0.10)" : "none",
  });
  return (
    <div role="tablist" aria-label="What Brill is writing" style={{ display: "inline-flex", gap: 2, padding: 3, borderRadius: 22, background: B.surface2, border: `1px solid ${B.border}`, flexShrink: 0 }}>
      <button role="tab" aria-selected={scope === "block"} style={seg(scope === "block")} onClick={() => setScope("block")}>{blockLabel}</button>
      <button role="tab" aria-selected={scope === "record"} style={seg(scope === "record")} onClick={() => setScope("record")}>{recordLabel}</button>
    </div>
  );
}


/**
 * @param kind          { id, label, hint, fields }
 * @param content       the block's stored content
 * @param onCommit      (content) => the saved record, or null on failure
 * @param ask           async ({ token, messages, content }) => Brill's reply text.
 *                      Throws a sentence she can read.
 * @param parse         (reply, content) => the merged content Brill proposes,
 *                      or null when the reply carries no block
 * @param strip         (reply) => the reply without its block
 * @param opener        Brill's first line
 * @param suggestions   { first: [...], later: [...] }
 * @param contextItems  the chips — [{ label, on }]
 * @param pager         { items: [{ id, label, written }], index, onGo(id), noun }
 * @param backLabel / onBack   the way back, drawn in this screen's one row at every width
 * @param picker / chromeInBar   ACCEPTED AND IGNORED since 2026-09-24: the panel's bar
 *                     stands down while she drafts, so there is no bar to hand them to
 * @param fieldProps    passed to SectionFields (word budgets)
 * @param title         the chat's title
 *
 * THE WHOLE-RECORD SCOPE (2026-09-16), optional:
 * @param record        {
 *   blockLabel, label,          the switch's two words ("Touch", "Sequence") — one word each, her ask
 *   noun, title, opener, suggestions,
 *   blocks: [{ id, kind, content, fieldProps? }],   every block, in order
 *   ask({ token, messages, contents }),  contents: { [id]: content }
 *   parse(reply, contents) => { [id]: merged content } | null
 *   strip(reply),
 *   onCommitBlock(id, content) => saved | null,
 *   onCommitAll(ready) => saved | null,  optional — ONE write for every block
 *                      the lock fills. A course module's blocks are columns of
 *                      one row, so a write per block would each carry the row
 *                      as it was and the last would undo the others.
 *   kindOf(id),        optional — the kind for a block the reply fills that
 *                      the record does not have yet (a module gains pre-work)
 *   empty,             optional — what the fields pane says with no blocks
 *   contextItems,      optional — the chips for THIS scope, when the whole
 *                      record sends something the one block does not
 * }
 * @param startScope    "block" | "record" — which one opens
 * @param nextRecord    { label, noun, go() } — the next sequence / page /
 *                      module, where Next goes once nothing is left here
 */
export function Drafter({
  kind, content: stored, onCommit, ask, parse, strip,
  opener, suggestions = {}, contextItems = [], pager = null,
  backLabel = "← Back", onBack, picker = null, chromeInBar = false,
  /* WHERE SHE IS: "Homepage · Banner". Its own line, above the controls.

     Four panels already showed this and were passing it as `picker`, which is
     the slot for a CONTROL — so on Sites, whose picker is the real page
     dropdown, there was nowhere for it and the screen never said which page or
     which section she was in. Her report, 2026-09-18: "it doesn't say the page
     or section." A string rather than a node, so the five panels cannot style
     the same line five ways.

     IT NAMES THE RECORD ONLY — "Homepage", "Prequalifier". The block is added
     HERE, because the scope switch is inside this component: a panel building
     "Homepage · Banner" itself cannot know she has just flipped to the whole
     page, and it said "Homepage · Other" while drafting the whole thing. */
  where = null,
  fieldProps = {}, title,
  record = null, startScope = "block", nextRecord = null,
  /* WHICH LIBRARY KINDS THIS SCREEN CAN HAND BRILL as background — ["story"]
     on Sites and decks, ["objection"] on Scripts, both plus testimonials on
     Outreach. Her rule, 2026-09-17: a QUOTE goes in word for word through the
     fields; a story or an objection is CONTEXT and is never pasted, "because
     it's meant to be quick". Absent means no picker on this screen.
     SINCE 2026-09-28 IT ONLY SWITCHES THE PICKER ON: the picker offers EVERY
     kind with a filter row (her call — "it should probably just always be
     everything and then it can be selected"), since every kind arrives here
     the same way, as background Brill never pastes. */
  libraryKinds = null,
  /* RECORDING THE VOICE (supabase/0030): where a lock lands, so Brill's draft
     is kept beside what she makes of it. { tool, recordType, blockId, recordOf }
     — `blockId` is the block this screen opened on; `recordOf(id)` maps a block
     id to { recordId, blockId } where the block is not its own row (a course
     module's blocks are columns of the module). Absent means nothing recorded. */
  voice = null,
  /* KEEPING THE CONVERSATION (supabase/0048 for Sites, 0049 for the other
     panels). Absent means the chat lives in the screen.
     { loaded, block, record, save(scope, { turns, pending }), problem, window }
     — `block` / `record` are what was kept for each scope, or null. Restored
     ONCE, when it arrives, and only if she has not started typing; a waiting
     draft comes back as the same lock card, never into her fields (rule 4). */
  keep = null,
}) {
  const [scope, setScope] = useState(record && startScope === "record" ? "record" : "block");

  /* What she has handed Brill for THIS conversation. Carried on the next
     message she sends, not stored on the record: it steers the writing, it is
     not part of it. Picking the same item twice is one use (`noteUse`), and the
     Library records where it went as soon as she picks. */
  const [handed, setHanded] = useState([]);
  /* Enough of an item to recognise it on a chip, not enough to read it there. */
  const shortText = (it) => {
    const words = String(it.title || it.text || "").trim().split(/\s+/).slice(0, 6).join(" ");
    return words.length < String(it.title || it.text || "").trim().length ? `${words}…` : words;
  };
  const usedAs = fieldProps?.usedAs || null;
  const inRecord = scope === "record" && !!record;

  const [content, setContent] = useState(stored || {});
  /* The whole record's fields, by block id — what the record scope edits. */
  const [contents, setContents] = useState(() => Object.fromEntries((record?.blocks || []).map((b) => [b.id, b.content || {}])));
  /* ONE CONVERSATION PER SCOPE. Switching does not throw either away, and a
     block-scope chat never becomes the history of a whole-record one. */
  const [blockMsgs, setBlockMsgs] = useState([{
    role: "assistant",
    content: opener || `I'm on the ${kind.label.toLowerCase()}. I can draft it from what you've locked, or you tell me the angle and I'll write to that. Which way?`,
  }]);
  const [recordMsgs, setRecordMsgs] = useState([{
    role: "assistant",
    content: record?.opener || "We're on the whole thing. Tell me what it needs to do, or say \"draft it all\" and I'll take a first pass.",
  }]);
  const msgs = inRecord ? recordMsgs : blockMsgs;
  const setMsgs = inRecord ? setRecordMsgs : setBlockMsgs;
  const [chatText, setChatText] = useState("");
  const [sending, setSending] = useState(false);
  /* What Brill last wrote, waiting on her — per scope. Nothing reaches the
     fields until she locks it (rule 4). */
  const [blockPending, setBlockPending] = useState(null);
  const [recordPending, setRecordPending] = useState(null);
  const pending = inRecord ? recordPending : blockPending;
  const twoPane = useTwoPane(1180);

  /* Bring back what was kept, once it has loaded. */
  /* Read through a ref: the restore runs once, when `loaded` turns true, and
     must see the chat as it stands at that moment. */
  const restored = useRef(!keep);
  const now = useRef({});
  now.current = { keep, blockMsgs, recordMsgs, record };
  const hasRecord = !!record;
  const keepLoaded = !!keep?.loaded;
  useEffect(() => {
    const { keep: k, blockMsgs: bm, recordMsgs: rm, record: rec } = now.current;
    if (!k || restored.current || !keepLoaded) return;
    restored.current = true;
    /* A whole-record draft kept across a visit can name a block she has since
       deleted. Only what can still land comes back: a card with nothing in it
       has nothing to lock. */
    const stillLands = (kept) => {
      if (!kept?.pending) return kept;
      const live = Object.entries(kept.pending).filter(([id]) => (rec?.blocks || []).some((b) => b.id === id) || rec?.kindOf?.(id));
      return { ...kept, pending: live.length ? Object.fromEntries(live) : null };
    };
    const back = (kept, msgsNow, setM, setP) => {
      if (!kept || msgsNow.some((m) => m.role === "user")) return;
      if (kept.turns?.length) setM(kept.turns);
      if (kept.pending) setP(kept.pending);
    };
    back(k.block, bm, setBlockMsgs, setBlockPending);
    if (hasRecord) back(stillLands(k.record), rm, setRecordMsgs, setRecordPending);
  }, [keepLoaded, hasRecord]);
  /* And write each scope as it changes. `save` skips a chat that holds only
     Brill's opener, and one that has not changed. */
  const keepSave = keep?.save;
  useEffect(() => {
    if (keepSave && restored.current) keepSave("block", { turns: blockMsgs, pending: blockPending });
  }, [keepSave, blockMsgs, blockPending]);
  useEffect(() => {
    if (keepSave && restored.current && hasRecord) keepSave("record", { turns: recordMsgs, pending: recordPending });
  }, [keepSave, hasRecord, recordMsgs, recordPending]);

  /* THE FIELDS SAVE AS SHE LEAVES THEM, and this screen says so. "Saved" and
     silence look identical, so a blocked save names itself. */
  const [saving, setSaving] = useState(false);
  const [saveProblem, setSaveProblem] = useState("");

  /* HOW THE TWO PANES SHARE THE WIDTH — one number, three ways to set it. Below
     `twoPane` there is no room for two, so it is always full. */
  const [ratio, setRatio] = useSplitRatio();
  const full = !twoPane || ratio >= SPLIT_FULL;
  /* One pane because the screen is narrow, rather than because she chose full
     width — the same line decides the chrome, the height and where the back
     lives, so they cannot drift apart. */
  const phone = !twoPane;
  const [pane, setPane] = useState("chat");
  const splitRef = useRef(null);
  const modeControl = twoPane ? <SplitModeControl ratio={ratio} onChange={setRatio} /> : null;
  const { session } = useSession();
  const recordVoice = useVoiceRecorder();
  /* Never awaited by the lock: her words are saved whether or not this lands. */
  const keepDraft = (id, draft, replaced) => {
    if (!voice) return;
    const where = voice.recordOf ? voice.recordOf(id) : { recordId: id, blockId: null };
    recordVoice({ tool: voice.tool, recordType: voice.recordType, ...where, how: "lock", draft, replaced });
  };
  const noun = inRecord ? (record.noun || "record") : kind.label.toLowerCase();

  /* NEXT, beside Send, in BOTH scopes. HIDDEN WHILE A DRAFT IS WAITING in the
     lock card, and while Brill is replying: a way onward beside a draft she has
     not locked is the fork where one branch loses her work (`292b62d`).

     In the whole-record scope there is no "next block" — the whole thing is
     the block — so it is the way back out, and says "All written" when every
     block has words. Her report, 2026-09-16: "the next didn't land next to
     send in sequences." It had been left off this scope on purpose; the
     button being there, always, is what she is used to from the Accelerator. */
  const hasWords = (c) => Object.values(c || {}).some((v) => (Array.isArray(v) ? v.length : String(v || "").trim()));
  /* WHERE IT GOES, in order (her call, 2026-09-16 — "next could also go to the
     next one"): the next unwritten block here; when there is none, the NEXT
     RECORD — the next sequence, page or module — so she keeps moving the way
     the Accelerator moves her; only when this is the last record, back out. */
  const here = inRecord
    ? { kind: "back", allWritten: (record.blocks || []).length > 0 && (record.blocks || []).every((b) => hasWords(contents[b.id])) }
    : nextStep(pager);
  const step = here && here.kind === "back" && nextRecord ? { kind: "record", label: nextRecord.label } : here;
  const backWords = String(backLabel || "Back").replace(/^←\s*/, "");
  const stepLabel = !step ? "" : step.kind === "block"
    ? `Next: ${step.label}`
    : step.kind === "record"
      ? `Next ${nextRecord.noun || "one"}: ${step.label}`
      : `${step.allWritten ? "All written — " : ""}${backWords}`;
  /* AN ARROW, NOT A SENTENCE (her ask: "too many words right now"). Where it
     goes is its tooltip and its accessible name. Forward is an arrow; the way
     back out is the back-and-up arrow. */
  /* ONE function, used by the arrow beside Send AND by the footer under the
     fields. Two buttons meaning "move on" must not be two definitions of where
     ON is — "what is next?" having more than one answer is the shape that has
     broken this app repeatedly. */
  const goStep = () => (step.kind === "block" ? pager.onGo(step.id) : step.kind === "record" ? nextRecord.go() : onBack?.());
  const nextButton = !step || pending || sending ? null : (
    <button
      onClick={goStep}
      title={stepLabel} aria-label={stepLabel}
      style={{ ...btnGhost, padding: "0 12px", minWidth: 40, display: "inline-flex", alignItems: "center", justifyContent: "center", color: B.teal, borderColor: B.tealLine, flexShrink: 0 }}>
      {step.kind === "back" ? <CornerUpLeft size={16} /> : <ArrowRight size={16} />}
    </button>
  );

  const runSave = async (write) => {
    setSaving(true);
    setSaveProblem("");
    const saved = await write();
    setSaving(false);
    if (!saved) setSaveProblem("That didn't save — check your connection");
  };
  const commit = async (next) => {
    if (JSON.stringify(next) === JSON.stringify(stored || {})) return;
    await runSave(() => onCommit(next));
  };
  const change = (next, immediate) => {
    setContent(next);
    if (immediate) commit(next);
  };
  const storedOf = (id) => (record?.blocks || []).find((b) => b.id === id)?.content || {};
  const commitBlock = async (id, next) => {
    if (JSON.stringify(next) === JSON.stringify(storedOf(id))) return;
    await runSave(() => record.onCommitBlock(id, next));
  };

  /* Prepended to the message she sends, so it arrives as part of what she is
     asking rather than as a turn she did not type. The guides already read a
     bracketed block as read-only context. */
  const libraryBlock = () => {
    if (!handed.length) return "";
    const lines = handed.map((it) => {
      const what = kindOf(it.kind).label;
      const who = it.kind === "testimonial" && it.who ? ` (${it.who})` : "";
      const title = it.title ? `${it.title}: ` : "";
      return `${what}${who} — ${title}${String(it.text || "").trim()}`;
    });
    return [
      "[FROM HER LIBRARY — background only. Use it to steer what you write.",
      "Never paste it back word for word, and never quote it as if she said it here.]",
      ...lines,
      "",
    ].join("\n");
  };

  const send = async (text) => {
    const msg = (text || "").trim();
    if (!msg || sending) return;
    const next = [...msgs, { role: "user", content: `${libraryBlock()}${msg}` }];
    const scopeNow = inRecord;
    setMsgs(next);
    setChatText("");
    setSending(true);
    /* A kept chat can run for weeks; what is SENT stays capped. */
    const toSend = keep?.window ? keep.window(next) : next;
    try {
      const token = await session?.getToken();
      if (scopeNow) {
        const reply = await record.ask({ token, messages: toSend, contents });
        const proposed = record.parse(reply, contents);
        if (proposed) setRecordPending(proposed);
        setRecordMsgs((m) => [...m, {
          role: "assistant",
          content: record.strip(reply) || (proposed ? `Have a read — lock it in and it goes into the ${noun}.` : "Try that again for me?"),
        }]);
      } else {
        const reply = await ask({ token, messages: toSend, content });
        const proposed = parse(reply, content);
        if (proposed) setBlockPending(proposed);
        setBlockMsgs((m) => [...m, {
          role: "assistant",
          content: strip(reply) || (proposed ? `Have a read — lock it in and it goes into the ${noun}.` : "Try that again for me?"),
        }]);
      }
    } catch (err) {
      /* A 429 or a 403 already carries a written explanation. */
      (scopeNow ? setRecordMsgs : setBlockMsgs)((m) => [...m, { role: "assistant", content: err?.message || "I couldn't reach Brill just then — check your connection and try again. Nothing you've written is lost." }]);
    } finally {
      setSending(false);
    }
  };

  /* The lock card. In the record scope it lists every block the reply would
     fill, and one press fills them all. */
  /* Every block the waiting reply fills, in the record's order, then any it
     would ADD — those are written in too, not dropped. */
  const pendingBlocks = !recordPending || !record ? [] : [
    ...(record.blocks || []).filter((b) => recordPending[b.id]),
    ...Object.keys(recordPending)
      .filter((id) => !(record.blocks || []).some((b) => b.id === id))
      .map((id) => ({ id, kind: record.kindOf?.(id) }))
      .filter((b) => b.kind),
  ];
  const lockCard = !pending ? null : inRecord ? (
    <SuggestedCopy target={`this ${noun}`}
      parts={pendingBlocks.map((b) => ({ kind: b.kind, content: recordPending[b.id] }))}
      onLock={async () => {
        const filled = pendingBlocks;
        setPane("fields");
        setContents((c) => ({ ...c, ...recordPending }));
        const ready = Object.fromEntries(filled.map((b) => [b.id, recordPending[b.id]]));
        for (const b of filled) keepDraft(b.id, ready[b.id], contents[b.id] || storedOf(b.id));
        setRecordPending(null);
        if (record.onCommitAll) await runSave(() => record.onCommitAll(ready));
        else for (const b of filled) await commitBlock(b.id, ready[b.id]);
        setRecordMsgs((m) => [...m, { role: "assistant", content: `That's locked into ${filled.map((b) => b.kind.label).join(", ")}. Change any of it in the fields — nothing is fixed.` }]);
      }}
      onDismiss={() => setRecordPending(null)} />
  ) : (
    <SuggestedCopy kind={kind} content={blockPending}
      onLock={() => {
        /* Flip to the fields at full width: she pressed Lock to SEE what she got. */
        setPane("fields");
        setContent(blockPending);
        /* What the fields held as she pressed Lock — her edits included — is
           what this draft replaced. */
        keepDraft(voice?.blockId, blockPending, content);
        commit(blockPending);
        setBlockPending(null);
        setBlockMsgs((m) => [...m, { role: "assistant", content: `That's locked into your ${noun}. Change any of it in the fields — nothing is fixed.` }]);
      }}
      onDismiss={() => setBlockPending(null)} />
  );

  const chatSuggestions = inRecord
    ? (msgs.length === 1 ? (record.suggestions?.first || ["Draft it all", "I'll tell you what it's for"]) : (record.suggestions?.later || ["Make them fit together", "Shorter", "Make it sound more like me"]))
    : (msgs.length === 1
      ? (suggestions.first || ["Draft it from what I've locked", "I'll tell you the angle"])
      : (suggestions.later || ["Shorter", "Make it sound more like me", "Give me another version"]));

  return (
    <>
      {/* ONE ROW, AND IT IS THE ONLY ONE — the way back, where she is, the
          scope, the panes, the width. Her list for a phone, 2026-09-22: "we can
          put back to page as an arrow, no site dropdown in mobile in the chat,
          no add page here. it should just be one line at the top so you can
          see the chat." And on 2026-09-24, for a laptop: "the chat window is
          TINY, all this gunk up top is really too much." So the panel's bar,
          the crumb and the heading stand down at EVERY width while she drafts
          (MomentumBar, and `bare` in each panel), and this row is all there is
          above the chat.

          The way back is ALWAYS this screen's now. `chromeInBar` used to hand
          it to the panel's bar on a wide screen; that bar no longer renders
          here, so the prop is accepted and ignored — the failure to guard is
          no way back at all, and oneWayBack / draftingBackInBar pin that there
          is exactly one.

          On a phone the words shrink to an arrow and ONE word of where: the
          block while she writes a block ("Banner"), the record while she
          writes the whole thing ("Homepage"). Both halves did not fit — 116px
          of words in 98px of row read "Homepage · Ba…" — and the chat card's
          own header, which used to name the block, is gone (her call: "take
          out reading your locked content for one more line"). */}
      <div style={{ display: "flex", alignItems: "center", gap: phone ? 6 : 10, marginBottom: phone ? 10 : 12, minWidth: 0 }}>
        {onBack && (phone ? (
          <button onClick={onBack} aria-label={backWords} title={backWords}
            style={{ ...btnGhost, padding: 8, lineHeight: 0, flexShrink: 0 }}>
            <ArrowLeft size={16} />
          </button>
        ) : (
          <button onClick={onBack} style={{ ...btnGhost, padding: "8px 14px", fontSize: 12.5, flexShrink: 0 }}>{backLabel}</button>
        ))}
        {where && (
          <span style={{ fontSize: phone ? 12 : 13, fontWeight: 600, color: B.mid, minWidth: 0, flex: phone ? 1 : "0 1 auto", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {phone ? (inRecord ? where : kind.label) : (inRecord ? where : `${where} · ${kind.label}`)}
          </span>
        )}
        {record && <ScopeSwitch compact={phone} scope={scope} setScope={setScope} blockLabel={record.blockLabel || "Block"} recordLabel={record.label || "Record"} />}
        {full && <PaneToggle icons={phone} pane={pane} setPane={setPane} />}
        {!phone && <span style={{ flex: 1 }} />}
        {modeControl}
      </div>

      {/* Chat on the LEFT, the fields it writes into on the right. */}
      <div ref={splitRef} style={{
        display: "grid",
        gridTemplateColumns: full ? "1fr" : `minmax(0, ${ratio}fr) 14px minmax(0, ${1 - ratio}fr)`,
        gap: full ? 0 : 8, alignItems: "start",
      }}>
        <div style={{ minWidth: 0, display: full && pane !== "chat" ? "none" : "block" }}>
          {/* "Kept" and "not kept" look identical until she comes back, so
              the not-kept case says so. */}
          {keep?.problem && (
            <div style={{ marginBottom: 8 }}><SavedTick problem={keep.problem} /></div>
          )}
          <ChatPane
            tone="plain"
            title={inRecord ? (record.title || `Write the whole ${noun}`) : (title || `Write the ${noun}`)}
            msgs={msgs} sending={sending} chatText={chatText} setChatText={setChatText} onSend={send}
            contextItems={[
              ...(inRecord && record.contextItems ? record.contextItems : contextItems),
              ...handed.map((it) => ({ label: `${kindOf(it.kind).label}: ${shortText(it)}`, on: true })),
            ]}
            footer={libraryKinds && libraryKinds.length ? (
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", padding: "8px 14px", borderTop: `1px solid ${B.border}` }}>
                <LibraryPickButton kinds={ALL_LIBRARY_KINDS} requirePermission usedAs={usedAs}
                  label={handed.length ? "Add another" : "From your library"}
                  onPick={(item) => setHanded((h) => (h.some((x) => x.id === item.id) ? h : [...h, item]))} />
                {handed.length > 0 && (
                  <button onClick={() => setHanded([])}
                    style={{ ...btnGhost, padding: "7px 12px", fontSize: 12 }}>
                    Clear {handed.length === 1 ? "it" : "them"}
                  </button>
                )}
                {/* NOT while nothing is handed over — at any width since
                    2026-09-24, a phone only before that: a caption explaining
                    a button she has already used (rule 9), in the screen where
                    every line is the conversation's. Once she HAS handed
                    something over it comes back, because that sentence is
                    about what Brill will do with it. */}
                {handed.length > 0 && (
                  <span style={{ fontSize: 11.5, color: B.dim, lineHeight: 1.5 }}>
                    {handed.length
                      ? "Brill writes from these — it never pastes them."
                      : "Hand Brill a story or an objection to write from."}
                  </span>
                )}
              </div>
            ) : null}
            height={phone ? CHAT_HEIGHT_PHONE : CHAT_HEIGHT_DRAFTING}
            headless
            foldContext
            beside={nextButton}
            pending={lockCard}
            placeholder={inRecord ? "Tell Brill what the whole thing needs…" : "Tell Brill the angle, or what to change…"}
            suggestions={chatSuggestions}
          />
        </div>

        {!full && <SplitDragBar containerRef={splitRef} onRatio={setRatio} />}

        <div style={{ border: `1px solid ${B.border}`, borderRadius: 14, background: B.surface, minWidth: 0, overflow: "hidden",
          display: full && pane === "chat" ? "none" : "flex", flexDirection: "column",
          /* THE SAME HEIGHT AS THE CHAT, so the two footers line up — her
             condition: "it would need to line up with the send button to feel
             ok". The chat is a clamped height so Send stays on screen, while
             the fields just grew with their content, which is why the two only
             ever lined up by accident. NOT on a phone: there is one pane there,
             and a fixed-height scroller inside a scrolling page is two
             scrollbars fighting. */
          ...(full ? {} : { height: CHAT_HEIGHT_DRAFTING }) }}>
          {/* What this pane holds, and nothing about how the screen behaves —
              "What Brill drafts waits in the chat until you lock it in" was a
              caption for behaviour she has watched work (rule 9), and the
              width control moved up to the one row (2026-09-24). */}
          <div style={{ padding: "12px 18px", borderBottom: `1px solid ${B.border}`, background: B.surface2 }}>
            <h3 style={{ fontSize: 14.5, fontWeight: 700, margin: 0, color: B.text }}>
              {inRecord ? (record.heading || `The whole ${noun}`) : kind.label}
              {!inRecord && kind.hint && <span style={{ fontSize: 12, fontWeight: 400, color: B.dim, marginLeft: 8 }}>{kind.hint}</span>}
            </h3>
          </div>
          <div style={{ flex: 1, minHeight: 0, overflowY: full ? "visible" : "auto" }}>
          {inRecord ? (
            /* EVERY BLOCK, fields on screen — the record scope never hides what
               it is writing into. Each saves as she leaves it, like anywhere. */
            <div style={{ padding: "14px 18px 4px", display: "flex", flexDirection: "column", gap: 18 }}>
              {!(record.blocks || []).length && (
                <p style={{ fontSize: 13, color: B.dim, margin: "0 0 14px", lineHeight: 1.6 }}>{record.empty || "Nothing here yet — what Brill writes lands here when you lock it in."}</p>
              )}
              {(record.blocks || []).map((b) => (
                <div key={b.id}>
                  <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: B.mid, marginBottom: 8 }}>{b.kind.label}</div>
                  <SectionFields kind={b.kind} content={contents[b.id] || {}}
                    onChange={(next, immediate) => { setContents((c) => ({ ...c, [b.id]: next })); if (immediate) commitBlock(b.id, next); }}
                    onBlur={() => commitBlock(b.id, contents[b.id] || {})} {...(b.fieldProps || {})} />
                </div>
              ))}
            </div>
          ) : (
            <div style={{ padding: "18px 18px 4px" }}>
              <SectionFields kind={kind} content={content} onChange={change} onBlur={() => commit(content)} {...fieldProps} />
            </div>
          )}
          </div>

          {/* WHERE SHE FINISHES. Her read, 2026-09-18: "it feels unclear it's
              saved and how to move on." Both answers were already on screen and
              neither was where her eye lands — the tick sat in the card header
              competing with the pane icons, and moving on was a pager in the far
              top corner or an unlabelled arrow beside Send.

              THE WORDS MATTER MOST AT THE END. The arrow beside Send is bare
              because she asked for fewer words; here it carries `stepLabel`,
              because of what she reported about the Accelerator: "they REALLY
              didn't know what to do if there wasn't a next". So the last
              section shows no dead arrow — it says "All written — Back to the
              page" and IS the door. Rule 11, in the place she is looking when
              she runs out of sections. */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap",
            padding: "12px 18px", borderTop: `1px solid ${B.border}`, background: B.surface2, flexShrink: 0 }}>
            <SavedTick saving={saving} problem={saveProblem} />
            <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
              {!inRecord && pager && pager.items.length > 1 && (
                <BlockPager items={pager.items} index={pager.index} noun={pager.noun}
                  onGo={(i) => { const it = pager.items[i]; if (it) pager.onGo(it.id); }} />
              )}
              {step && (
                <button onClick={goStep} title={stepLabel}
                  style={{ ...btnGhost, padding: "9px 14px", fontSize: 12.5, fontWeight: 700, color: B.teal, borderColor: B.tealLine,
                    display: "inline-flex", alignItems: "center", gap: 7, flexShrink: 0 }}>
                  {step.kind === "back" ? <CornerUpLeft size={14} /> : null}
                  {stepLabel}
                  {step.kind === "back" ? null : <ArrowRight size={14} />}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
